// Quotation status registry — `code -> { label, tone }`, the quotation-side twin of
// order-status.js.
//
// Covers every value of the live Postgres `enum_quotations_status`, which currently has
// TWENTY values. The prototype's status dropdown hardcodes eighteen and is already
// stale: QUOTE_SHARED and QUOTATION_SHARED were added afterwards and are missing from
// it. That is exactly why this is built from the enum rather than transcribed from the
// prototype — and why the filter list is fetched from the server at runtime rather than
// read from this file.
//
// This file's job is only labels and tones. It deliberately does NOT decide which
// statuses are selectable; the server's live enum does.
//
// A note on what these statuses mean in practice, which matters when reading counts:
// `CONVERTED_TO_ORDER` is unreliable as a conversion signal. Only ~12% of genuinely
// converted quotations carry it — 4,535 quotations have an `orderId` but still read
// QUOTATION_PENDING. Use `orderId IS NOT NULL` to detect conversion, never this status.
// See pillup-api/docs/tat-dashboard/reference-status-vocabularies.md.

/** @typedef {"success" | "warning" | "info" | "error" | "neutral"} StatusTone */

export const QUOTATION_STATUS_REGISTRY = {
    // --- Open / in flight -------------------------------------------------
    QUOTATION_ACTIVE: { label: "Active", tone: "info" },
    QUOTATION_PENDING: { label: "Pending", tone: "warning" },
    QUOTATION_UPLOADRX: { label: "Rx Upload Pending", tone: "warning" },

    // --- Contact attempts --------------------------------------------------
    CALL_PENDING: { label: "Call Pending", tone: "warning" },
    CALL_PICKED: { label: "Call Picked", tone: "info" },

    // --- Prescription exchange ---------------------------------------------
    // RX_SENT and RX_SEND are both live enum values with the same apparent meaning —
    // near-certainly a typo that was never cleaned up. Both are labelled identically
    // so an ops user is never asked to distinguish them.
    RX_SENT: { label: "Rx Sent", tone: "info" },
    RX_SEND: { label: "Rx Sent", tone: "info" },

    // --- Quote delivery ----------------------------------------------------
    // Same duplicate-value situation as RX_SENT/RX_SEND. Both added 2026-08-27.
    QUOTE_SHARED: { label: "Quote Shared", tone: "info" },
    QUOTATION_SHARED: { label: "Quote Shared", tone: "info" },

    // --- Digital Rx confirmation -------------------------------------------
    USER_DRX_PENDING: { label: "DRX To Be Confirmed", tone: "warning" },
    USER_DRX_ACCEPTED: { label: "DRX Accepted", tone: "success" },
    USER_DRX_REJECTED: { label: "DRX Rejected", tone: "error" },

    // --- Payment -----------------------------------------------------------
    PAYMENT_PENDING: { label: "Payment Pending", tone: "warning" },
    PAYMENT_CONFIRMED: { label: "Payment Confirmed", tone: "success" },
    PAYMENT_FAILED: { label: "Payment Failed", tone: "error" },
    PAYMENT_CANCELLED: { label: "Payment Cancelled", tone: "error" },

    // --- Terminal ----------------------------------------------------------
    CONVERTED_TO_ORDER: { label: "Converted to Order", tone: "success" },
    QUOTATION_INACTIVE: { label: "Inactive", tone: "neutral" },
    QUOTATION_CANCELLED: { label: "Cancelled", tone: "error" },

    MANUAL_INTERVENTION: { label: "Needs Attention", tone: "error" },
};

/**
 * Look up a quotation status code's `{ label, tone }`. Unknown codes fall back to a
 * neutral tone with the raw code as the label, so a status added to the enum before
 * this file is updated still renders legibly instead of blank.
 *
 * @param {string | null | undefined} code
 * @returns {{ label: string, tone: StatusTone }}
 */
export function getQuotationStatusMeta(code) {
    if (!code) return { label: "—", tone: "neutral" };
    const entry = QUOTATION_STATUS_REGISTRY[code.toUpperCase?.() ?? code];
    if (entry) return entry;
    return { label: code, tone: "neutral" };
}
